import { RichTextEditorProps } from '@modules/common/components/RichTextEditor/RichTextEditor';
import { TextWidgetActions } from '@modules/dashboard/components/TextWidget/TextWidgetActions';
import { DASHBOARD_ACTIVE_CELL_ID } from '@modules/dashboard/constants';
import { getDashboardRow } from '@modules/dashboard/helpers';
import {
  useIsDashboardEditable,
  useUpsertDashboard,
} from '@modules/dashboard/hooks';
import { DashboardActionType } from '@modules/dashboard/store/dashboardActionType';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { Box, alpha } from '@mui/material';
import { EditorEvents } from '@tiptap/react';
import { DashboardCell } from 'generated/graphql';
import Lockr from 'lockr';
import { cloneDeep, debounce } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import lazyLoadWithRetry from 'v2/core/modules/lazyLoadWithRetry';

const RichTextEditor = lazyLoadWithRetry(
  () =>
    import(
      '@modules/common/components/RichTextEditor' /* webpackChunkName: "RichTextEditor" */
    ),
);

interface TextWidgetProps extends RichTextEditorProps {
  rowID: string;
  cell: DashboardCell;
  content: string;
}

export function TextWidget({
  rowID,
  cell,
  content,
  ...editorProps
}: TextWidgetProps) {
  const { upsertDashboard } = useUpsertDashboard();
  const textEditorRef = useRef<HTMLDivElement | null>(null);
  const [isEditorActive, setIsEditorActive] = useState(false);

  const dispatch = useDashboardStore(({ dispatch }) => dispatch);
  const metadata = useDashboardStore(({ metadata }) => metadata);

  const isEditable = useIsDashboardEditable();

  const cellMinimumHeightsByRowID = useDashboardStore(
    ({ visual }) => visual.cellMinimumHeightsByRowID,
  );
  const isUpdateDashboardInProgress = useDashboardStore(
    ({ visual }) => visual.isUpdateDashboardInProgress,
  );

  const dependencies = {
    dispatch,
    metadata,
    cell,
    cellMinimumHeightsByRowID,
    rowID,
    content,
    isUpdateDashboardInProgress,
    upsertDashboard,
  };
  const dependenciesRef = useRef(dependencies);
  dependenciesRef.current = dependencies;

  const activeCellID = Lockr.get(DASHBOARD_ACTIVE_CELL_ID);
  useEffect(() => {
    if (activeCellID === cell.id && dependenciesRef.current.content === '') {
      setIsEditorActive(true);
    }

    return () => {
      Lockr.rm(DASHBOARD_ACTIVE_CELL_ID);
    };
  }, [activeCellID, cell.id]);

  useEffect(() => {
    if (!textEditorRef.current) {
      return;
    }

    const editorResizeObserver = new ResizeObserver((entries) => {
      const { height } = entries[0].contentRect;

      const { rowID, cellMinimumHeightsByRowID, cell } =
        dependenciesRef.current;

      if (cellMinimumHeightsByRowID[rowID]?.[cell.id] !== height) {
        dependenciesRef.current.dispatch({
          type: DashboardActionType.SET_VISUAL,
          payload: {
            cellMinimumHeightsByRowID: {
              ...cellMinimumHeightsByRowID,
              [rowID]: {
                ...cellMinimumHeightsByRowID[rowID],
                [cell.id]: height,
              },
            },
          },
        });
      }
    });

    editorResizeObserver.observe(textEditorRef.current);

    return () => {
      editorResizeObserver.disconnect();
    };
  }, []);

  const onContentUpdate = useMemo(
    () =>
      debounce(async (params: EditorEvents['update']) => {
        const { editor } = params;

        const clonedMetadata = cloneDeep(dependenciesRef.current.metadata);
        const clonedRow = getDashboardRow({
          rowID: dependenciesRef.current.rowID,
          rows: clonedMetadata.layout.rows,
        });

        if (!clonedRow) {
          return;
        }

        const content = dependenciesRef.current.content;
        if (
          content === ''
            ? content !== editor.getText()
            : content !== editor.getHTML()
        ) {
          const updatedContent = editor.getHTML();

          if (dependenciesRef.current.isUpdateDashboardInProgress) {
            onContentUpdate(params);
            return;
          }

          await dependenciesRef.current.upsertDashboard({
            dashboardProps: {
              metadataV2: clonedMetadata,
              updateWidget: {
                id: dependenciesRef.current.cell.contentID,
                textWidget: {
                  text: updatedContent,
                },
              },
            },
            shouldRefetchDashboard: true,
          });
        }
      }, 1000),
    [],
  );

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        borderRadius: 1.5,
        position: 'relative',
        ':hover': isEditable
          ? {
              '.text-editor-actions': {
                visibility: 'visible',
              },
              backgroundColor: isEditorActive
                ? undefined
                : (theme) => alpha(theme.palette.background.paper, 0.8),
            }
          : undefined,
      }}
      onMouseDown={() => {
        setIsEditorActive(true);
      }}
      onBlur={() => {
        setIsEditorActive(false);
      }}
    >
      <Box
        className="rte-container"
        sx={{
          mr: 5,
          height: '100%',
          pl: 2,
        }}
      >
        <RichTextEditor
          {...editorProps}
          ref={textEditorRef}
          onUpdate={onContentUpdate}
          content={content}
          editable={isEditable}
          shouldFocus={isEditorActive && isEditable}
        />
      </Box>
      <TextWidgetActions
        isEditorActive={isEditorActive}
        sx={{ position: 'absolute', top: 8, right: 4 }}
        className="text-editor-actions"
        cell={cell}
        rowID={rowID}
        textContent={content}
      />
    </Box>
  );
}
